import { Link } from 'react-router-dom';
import type { ComponentState, ComponentView, DeploymentOutcome, ServingRelation } from '../../api';
import type { ComponentOutcome } from '../../api';
import { applicationTypeLabel } from '../../labels';
import { LoadingRows, StatusBadge } from '../ui';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { OutcomeBadge } from '../status/StatusPieces';
import { Ago } from '../status/ago';
import { outcomeOfStatus } from '../status/statusModel';
import { PanelCard, QuietEmpty, SectionHeader } from './ui';

/**
 * ComponentStatusPanel: one row per deployable component — the release it
 * serves (commit and age), how its latest deployment went, and what its own
 * site checks say. Each fact is its own column; none is repeated in a
 * sentence. A failed attempt beside a serving release reads as exactly that:
 * the outcome badge next to the release that is live.
 */
export function ComponentStatusPanel({
  states,
  views,
  loading,
  deploymentPath,
}: {
  states: ComponentState[] | undefined;
  views: ComponentView[];
  loading: boolean;
  deploymentPath: (id: string) => string;
}) {
  const viewOf = (id: string) => views.find((view) => view.application.id === id);
  // The derivation is the source; when it has no component states (a server
  // older than this panel), the components endpoint still says what is live.
  const rows = (states && states.length > 0 ? states : views.map(stateFromView)).filter((state) => !state.disabled);

  const columns: Column<ComponentState>[] = [
    {
      id: 'component',
      header: 'Component',
      card: 'title',
      width: 20,
      cell: (state) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">{state.name}</span>
          <span className="cell-stack__secondary">{applicationTypeLabel[state.type] ?? state.type}</span>
        </span>
      ),
    },
    {
      id: 'release',
      header: 'Release',
      width: 'fill',
      cell: (state) => {
        const view = viewOf(state.application_id);
        const release = view?.active_release;
        const commit = release?.commit_sha || state.live?.deployment?.commit_sha;
        const message = release?.commit_message || state.live?.deployment?.message;
        const since = state.live?.since ?? release?.created_at;
        if (!state.live && !release) return <span className="cell-stack__secondary">None</span>;
        const firstLine = message?.split('\n')[0];
        return (
          <span className="cell-stack">
            {/* While the message is not known the commit names the release (CO-152). */}
            <span className="cell-stack__primary cell-clamp">{firstLine || (commit ? <code translate="no">{commit.slice(0, 7)}</code> : 'Live')}</span>
            <span className="cell-stack__secondary">
              {commit && firstLine ? <code translate="no">{commit.slice(0, 7)}</code> : null}
              {since ? (
                <>
                  {commit && firstLine ? ' · ' : null}
                  <Ago iso={since} />
                </>
              ) : null}
            </span>
          </span>
        );
      },
    },
    {
      id: 'latest',
      header: 'Last deploy',
      width: 28,
      cell: (state) => <LatestAttempt state={state} deploymentPath={deploymentPath} />,
    },
    {
      id: 'site',
      header: 'Checks',
      card: 'status',
      width: 'status',
      cell: (state) =>
        state.site ? (
          state.site === 'unknown' ? <span className="cell-stack__secondary">Not checked</span> : <StatusBadge kind="siteState" value={state.site} />
        ) : (
          <StatusBadge kind="componentHealth" value={state.health || 'unknown'} />
        ),
    },
  ];

  return (
    <PanelCard labelledBy="overview-components" testId="component-status">
      <SectionHeader id="overview-components" title="Components" />
      {loading && rows.length === 0 ? (
        <LoadingRows label="Loading components" rows={3} rowHeight="44px" />
      ) : rows.length === 0 ? (
        <QuietEmpty>No components</QuietEmpty>
      ) : (
        <ResponsiveTable
          rows={rows}
          columns={columns}
          rowKey={(state) => state.application_id}
          caption="Components"
          rowLink={(state) => {
            const attempt = state.deploying ?? state.latest;
            return attempt ? { to: deploymentPath(attempt.id), accessibleName: `Open the ${attempt.commit_sha.slice(0, 7)} deployment of ${state.name}` } : null;
          }}
          rowProps={(state) => ({ testId: `component-status-${state.application_id}` })}
        />
      )}
    </PanelCard>
  );
}

/** Short words for what an attempt did to what serves; "serving now" is the baseline and says nothing. */
const servingWords: Partial<Record<ServingRelation, string>> = {
  superseded: 'Superseded',
  never_activated: 'Never activated',
  rolled_back: 'Switched back',
  pending: 'Not live yet',
};

/** The latest attempt: its outcome and commit, then only what is not the baseline. */
function LatestAttempt({ state, deploymentPath }: { state: ComponentState; deploymentPath: (id: string) => string }) {
  const attempt = state.deploying ?? state.latest;
  if (!attempt) return <span className="cell-stack__secondary">None</span>;
  const outcome: DeploymentOutcome = state.deploying ? 'deploying' : (attempt.outcome ?? outcomeOfStatus(attempt.status, attempt.failure_reason));
  const previous = !state.deploying && outcome === 'failed' && state.outcome === 'failed_previous_live';
  const baseline = !state.deploying && attempt.serving === 'serving';
  const notes = [
    // The attempt that made the serving release: its commit and age are in the Release column.
    baseline ? null : previous ? 'Previous live' : attempt.serving ? servingWords[attempt.serving] : null,
    state.failed_in_a_row && state.failed_in_a_row > 1 && !state.deploying ? `${state.failed_in_a_row}× failed` : null,
  ].filter(Boolean);
  return (
    <span className="cell-stack">
      <Link to={deploymentPath(attempt.id)} className="cell-inline component-status__outcome" aria-label={`${attempt.commit_sha.slice(0, 7)} deployment of ${state.name}`}>
        <OutcomeBadge outcome={outcome} />
        {baseline ? null : <code translate="no">{attempt.commit_sha.slice(0, 7)}</code>}
      </Link>
      {baseline ? null : (
        <span className="cell-stack__secondary">
          {notes.map((note) => (
            <span key={note}>{note} · </span>
          ))}
          <Ago iso={attempt.completed_at ?? attempt.created_at} />
        </span>
      )}
    </span>
  );
}

/** A component's state from the components endpoint alone. */
function stateFromView(view: ComponentView): ComponentState {
  const last = view.last_deployment ?? undefined;
  const live = view.active_release ? { release_id: view.active_release.id, since: view.active_release.created_at } : undefined;
  let outcome: ComponentOutcome = live ? 'live' : 'never_deployed';
  if (last?.status === 'failed') outcome = live && live.release_id !== last.release_id ? 'failed_previous_live' : 'failed_not_live';
  else if (last?.status === 'rolled_back') outcome = 'rolled_back';
  else if (last?.status === 'completed_with_failures') outcome = 'partial';
  return {
    application_id: view.application.id,
    name: view.application.name,
    type: view.application.type,
    disabled: Boolean(view.application.disabled),
    health: view.health,
    live,
    latest: last ? { id: last.id, commit_sha: last.source_commit_sha, status: last.status, created_at: last.created_at } : undefined,
    outcome,
  };
}
